import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  signal,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { AbbCommonModule } from 'app/abb-common.module';
import { ToastService } from '@services/ui/toast.service';
import { URLS } from '@constants/urls';
import { AbbCardComponent } from '@components/abb-card/abb-card.component';
import { SignupFormComponent } from './signup-form/signup-form.component';
import { TranslateService } from '@ngx-translate/core';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CognitoService, IUser } from '@services/cognito/cognito.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { getErrorMessage } from '@utils/errors';
import { Apollo } from 'apollo-angular';

@Component({
  selector: 'abb-signup',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatCardModule,
    TPipe,
    AbbCommonModule,
    AbbCardComponent,
    SignupFormComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
  ],
  template: `
    <div class="relative block overflow-y-auto w-full h-full">
      <!-- Video background -->
      <video
        #backgroundVideo
        class="absolute top-0 left-0 w-full h-full object-cover -z-10"
        autoplay
        muted
        loop
        playsinline
        preload="auto"
        (loadeddata)="onVideoLoaded()"
        (canplay)="onVideoCanPlay()"
      >
        <source
          src="https://new.abb.com/videos/librariesprovider84/smart-buildings/insite-energy-pro-loop2.mp4?sfvrsn=99f7670d_4"
          type="video/mp4"
        />
        <!-- Fallback per browser che non supportano il video -->
        <div
          class="absolute top-0 left-0 w-full h-full bg-center bg-cover bg-no-repeat"
          style="background-image: url(/assets/images/abb-bg.png);"
        ></div>
      </video>
      <abb-card
        [cardTitle]="'Account Registration' | t"
        [cardHeight]="isConfirmed ? 'h-auto' : undefined"
      >
        <ng-container card-content>
          @if(!isConfirmed) {
          <abb-signup-form
            #signupFormRef
            (formSubmit)="signup($event)"
            [checkFirstStepValidation]="checkFirstStepValidation"
            [twoStepsRegistration]="twoStepsRegistration"
            [secondStepRegistration]="secondStepRegistration"
            (firstStepValidationPassed)="goToSecondStep($event)"
          ></abb-signup-form>
          }@else {
          <p class="mb-4">
            {{
              'A confirmation code has been sent to your email. Please enter the code below to verify your account'
                | t
            }}.
          </p>
          <form
            id="confirm-signup-form"
            [formGroup]="confirmForm"
            (ngSubmit)="confirmSignUp()"
          >
            <mat-form-field class="w-full" color="primary">
              <mat-label>{{ 'Code' | t }}</mat-label>
              <input
                matInput
                formControlName="code"
                name="code"
              />@if(confirmForm.get('code')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
          </form>
          }
        </ng-container>
        <ng-container card-actions>
          @if(!isConfirmed) {
          <div
            [ngClass]="
              (secondStepRegistration | async)
                ? 'w-full flex flex-row items-center' +
                  (!(twoStepsRegistration | async)
                    ? ' justify-center'
                    : ' justify-end')
                : ''
            "
          >
            @if(secondStepRegistration | async) {
            <abb-button
              [ngClass]="{ hidden: !(twoStepsRegistration | async) }"
              [variant]="'clean'"
              class="w-44"
              (onclick)="goToFirstStep()"
              className="py-2.5 text-sm font-medium"
              [disabled]="loading"
              >{{ 'Back' | t }}</abb-button
            >
            <abb-button
              [type]="'submit'"
              form="signup-form"
              class="w-44"
              className="py-2.5 text-sm font-medium"
              [loading]="loading"
              [disabled]="loading"
            >
              {{ 'Create Account' | t }}</abb-button
            >
            } @else {
            <abb-button
              class="w-44"
              (onclick)="validateFirstStep()"
              className="py-2.5 text-sm font-medium"
              >{{ 'Next' | t }}</abb-button
            >
            }
          </div>
          } @else {
          <div class="w-full flex flex-row justify-end items-center">
            <abb-button
              [type]="'submit'"
              form="confirm-signup-form"
              [disabled]="!confirmForm.valid || loading"
              [loading]="loading"
              class="w-44"
              className="py-2.5 text-sm font-medium"
            >
              {{ 'Confirm' | t }}</abb-button
            >
          </div>
          }
        </ng-container>
      </abb-card>
    </div>
  `,
  styles: ``,
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'signup',
    },
  ],
  host: { class: 'block w-full h-full flex-1' },
})
export class SignUpComponent implements AfterViewInit, OnDestroy {
  @ViewChild('signupFormRef') signupFormRef!: SignupFormComponent;
  @ViewChild('backgroundVideo', { static: false })
  backgroundVideo!: ElementRef<HTMLVideoElement>;

  prefix = inject(TRANSLATION_PREFIX);
  twoStepsRegistration: BehaviorSubject<boolean> = new BehaviorSubject(true);
  secondStepRegistration: BehaviorSubject<boolean> = new BehaviorSubject(true);
  checkFirstStepValidation: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );

  loading: boolean;
  isConfirmed: boolean;
  static pendingImgUrl: [email: string, imgUrl: string] | null; // used to upload license-specific avatar after signup in login.component.ts
  user: IUser;
  confirmForm = new FormGroup({
    code: new FormControl('', [Validators.required]),
  });
  userEmail = signal<string | undefined>(undefined);
  licenseId = signal<string | undefined>(undefined);

  private subscriptions: Subscription[] = [];
  constructor(
    private readonly apollo: Apollo,
    private readonly cognitoService: CognitoService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly translate: TranslateService,
    private readonly toast: ToastService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.loading = false;
    this.isConfirmed = false;
    this.user = {} as IUser;
    this.subscriptions.push(
      this.route.queryParamMap.subscribe((params) => {
        const invitation = params.get('invitation');
        const splittedInvitation = invitation?.split('#');
        this.userEmail.set(splittedInvitation?.[1] || undefined);
        this.licenseId.set(splittedInvitation?.[3] || undefined);
      })
    );
  }

  ngAfterViewInit(): void {
    if (this.userEmail() && this.licenseId()) {
      this.twoStepsRegistration.next(false);
      this.secondStepRegistration.next(true);
      this.signupFormRef.form.controls.registrationCompany?.enable();
      this.signupFormRef.form.patchValue({
        email: this.userEmail(),
        registrationCompany: this.licenseId(),
      });
      this.signupFormRef.form.controls.email.disable();
      this.signupFormRef.form.controls.company.disable();
      this.signupFormRef.form.controls.country?.disable();
      this.signupFormRef.form.controls.city?.disable();
      this.signupFormRef.form.controls.postalCode?.disable();
      this.signupFormRef.form.controls.address?.disable();
      this.signupFormRef.form.controls.fullAddress?.disable();
      this.signupFormRef.form.updateValueAndValidity();
      this.cdr.detectChanges();
    }

    // Gestione del video di background
    this.ensureVideoPlays();
  }

  onVideoLoaded(): void {
    // Il video è stato caricato, prova a farlo partire
    this.ensureVideoPlays();
  }

  onVideoCanPlay(): void {
    // Il video può essere riprodotto, prova a farlo partire
    this.ensureVideoPlays();
  }

  private ensureVideoPlays(): void {
    if (this.backgroundVideo?.nativeElement) {
      const video = this.backgroundVideo.nativeElement;
      video.muted = true; // Assicurati che sia muto per l'autoplay

      // Prova a far partire il video
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Video started successfully
            console.log('Background video started successfully');
          })
          .catch((error) => {
            console.warn('Background video autoplay was prevented:', error);
            // Fallback: prova a far partire il video su user interaction
            this.setupVideoInteractionFallback();
          });
      }
    }
  }

  private setupVideoInteractionFallback(): void {
    if (this.backgroundVideo?.nativeElement) {
      const video = this.backgroundVideo.nativeElement;

      // Handler per far partire il video al primo click/touch sulla pagina
      const startVideoOnInteraction = () => {
        video
          .play()
          .then(() => {
            console.log('Background video started after user interaction');
            // Rimuovi gli event listeners una volta che il video è partito
            document.removeEventListener('click', startVideoOnInteraction);
            document.removeEventListener('touchstart', startVideoOnInteraction);
          })
          .catch((error) => {
            console.warn(
              'Could not start background video even after user interaction:',
              error
            );
          });
      };

      // Aggiungi gli event listeners
      document.addEventListener('click', startVideoOnInteraction, {
        once: true,
      });
      document.addEventListener('touchstart', startVideoOnInteraction, {
        once: true,
      });
    }
  }

  goToFirstStep() {
    // this.secondStepRegistration.next(false);
    this.router.navigate([`${URLS.BASE}${URLS.LOGIN}`]);
  }

  validateFirstStep() {
    this.checkFirstStepValidation.next(true);
  }

  goToSecondStep(validationPassed: boolean) {
    this.secondStepRegistration.next(validationPassed);
  }

  signup(form: FormGroup): void {
    if (this.userEmail()) {
      this.user = { ...form.value, email: this.userEmail() };
    } else {
      this.user = form.value;
    }
    if (form.invalid) {
      return;
    }

    this.loading = true;

    // Prima controlla se l'email esiste già
    this.cognitoService
      .checkUserExists(this.user.email)
      .then((userExists) => {
        if (userExists) {
          // L'email è già in uso
          this.loading = false;
          this.toast.notifyError(
            this.translate.instant('signup.Email is already in use')
          );
          return;
        }

        // L'email non esiste, procedi con la registrazione
        return this.cognitoService.signUp(this.user);
      })
      .then((result) => {
        if (result) {
          this.loading = false;
          this.isConfirmed = true;
        }
      })
      .catch((err) => {
        this.loading = false;
        console.error('Signup error:', err);
        let errMsg = getErrorMessage(err, this.translate);
        this.toast.notifyError(errMsg);
      });
  }

  confirmSignUp(): void {
    if (this.confirmForm.invalid) {
      return;
    }
    this.loading = true;
    this.cognitoService
      .confirmSignUp(this.user, this.confirmForm.value.code!)
      .then(() => {
        SignUpComponent.pendingImgUrl = this.user.email && this.user.imgUrl ? [this.user.email, this.user.imgUrl] : null;
        this.router
          .navigate([`${URLS.BASE}${URLS.LOGIN}`], {
            queryParams: {
              setupTOTP: 0,
            },
          })
          .finally(() => {
            this.loading = false;
          });
      })
      .catch((err) => {
        this.loading = false;
        console.error('Signup confirmation error:', err);
        let errMsg = getErrorMessage(err, this.translate);
        this.toast.notifyError(errMsg);
      });
  }

  errorHandler(): void {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
